import { useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Search, LogOut, Settings, User, Zap, CircleHelp, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link } from "react-router-dom";
import { getRoleLabel } from "@/hooks/useRolePermissions";
import { NotificationBell } from "@/components/notifications";
import { QuickEntrySheet } from "@/components/financial/QuickEntrySheet";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useChangelogUnreadCount } from "@/hooks/useChangelog";

export function Topbar() {
  const { profile, currentTenant, currentRole, signOut } = useAuth();
  const [quickEntryOpen, setQuickEntryOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data: unreadCount = 0 } = useChangelogUnreadCount();

  const canQuickEntry = currentRole === "admin" || currentRole === "financeiro";

  const getInitials = (name: string | null) => {
    if (!name) return "U";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const handleQuickSave = async (data: {
    type: "receivable" | "payable";
    description: string;
    amount: number;
    due_date: string;
    category_id: string | null;
    cost_center_id: string | null;
    deal_id: string | null;
  }) => {
    if (!currentTenant?.id) return;
    const table = data.type === "receivable" ? "receivables" : "payables";
    const { error } = await supabase.from(table).insert({
      tenant_id: currentTenant.id,
      description: data.description,
      amount: data.amount,
      due_date: data.due_date,
      category_id: data.category_id,
      cost_center_id: data.cost_center_id,
      deal_id: data.deal_id,
      status: "open",
    } as any);
    if (error) throw error;
    toast.success("Lançamento rápido criado!");
    queryClient.invalidateQueries({ queryKey: ["receivables"] });
    queryClient.invalidateQueries({ queryKey: ["payables"] });
  };

  return (
    <>
      <header className="sticky top-0 z-[5] flex h-16 shrink-0 items-center gap-4 border-b border-border bg-surface/80 px-4 backdrop-blur-xl supports-[backdrop-filter]:bg-surface/70 md:px-6">
        <SidebarTrigger className="hidden md:flex" />

        <Link to="/app" className="flex items-center gap-2 md:hidden">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <span className="text-lg font-bold">☀</span>
          </div>
          <span className="font-bold">Solux CRM</span>
        </Link>

        <div className="hidden min-w-0 flex-1 md:flex md:max-w-md">
          <button
            type="button"
            onClick={() => {
              // Dispatch CMD+K to open CommandPalette
              document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }));
            }}
            className="flex w-full items-center gap-2 rounded-md border border-border/40 bg-muted/50 px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted"
          >
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left">Buscar em tudo...</span>
            <kbd className="pointer-events-none hidden rounded border border-border/60 bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground/60 sm:inline-block">⌘K</kbd>
          </button>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Quick entry button - admin/financeiro only */}
          {canQuickEntry && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-warning hover:text-warning hover:bg-warning/10"
                  onClick={() => setQuickEntryOpen(true)}
                >
                  <Zap className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Lançamento Rápido</TooltipContent>
            </Tooltip>
          )}

          <ThemeToggle />

          <NotificationBell />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="relative h-9 w-9" asChild>
                <Link to="/app/novidades">
                  <Sparkles className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Novidades</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9" asChild>
                <Link to="/app/ajuda">
                  <CircleHelp className="h-5 w-5" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Manual de Ajuda</TooltipContent>
          </Tooltip>


          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={profile?.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary">{getInitials(profile?.full_name)}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{profile?.full_name || "Usuário"}</p>
                  <Badge variant="secondary" className="w-fit mt-1 text-[10px]">{getRoleLabel(currentRole)}</Badge>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/app/configuracoes" className="cursor-pointer"><User className="mr-2 h-4 w-4" /><span>Meu Perfil</span></Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/app/configuracoes" className="cursor-pointer"><Settings className="mr-2 h-4 w-4" /><span>Configurações</span></Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={signOut} className="cursor-pointer text-destructive"><LogOut className="mr-2 h-4 w-4" /><span>Sair</span></DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {canQuickEntry && (
        <QuickEntrySheet
          open={quickEntryOpen}
          onOpenChange={setQuickEntryOpen}
          onSave={handleQuickSave}
        />
      )}
    </>
  );
}
